import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, Save, Send } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { newKey } from "@/lib/utils";
import { fmtDateTime } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { canWrite } from "@/nav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader, Panel } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { FieldInput, useRefOptions, invalidateRef } from "@/components/FieldInput";
import { Attachments } from "@/components/Attachments";
import { FormFields, compact, FORM_DIALOG } from "@/components/RecordDialog";
import { DetailGrid } from "@/components/PageHeader";
import { PrintButton } from "@/components/PrintButton";

const GRN_MANUAL = [
  { section: "Vehicle & PO", name: "vehicle_no", label: "Vehicle No.", required: true },
  { section: "Vehicle & PO", name: "gate_entry_time", label: "Gate Entry Time", type: "datetime", required: true },
  { section: "Vehicle & PO", name: "po_ref", label: "PO Reference", type: "ref", ref: "purchase_orders", ref_label: "code", required: true },
  { section: "Vehicle & PO", name: "vendor", label: "Vendor (auto from PO)", type: "ref", ref: "vendors" },
];
const GRN_EXTRA = [
  { section: "Receipt documents", name: "site", label: "Site", type: "select", options: ["My Home Avali", "My Home Akrida", "Tellapur Technocity"] },
  { section: "Receipt documents", name: "challan_no", label: "Delivery Challan No." },
  { section: "Receipt documents", name: "invoice_no", label: "Vendor Invoice No." },
  { section: "Receipt documents", name: "transporter", label: "Transporter" },
  { section: "Receipt documents", name: "received_by", label: "Received By" },
  { section: "Receipt documents", name: "remarks", label: "Remarks", type: "textarea" },
];

const CHECKS = [
  ["qty_verified", "Quantity verified against PO and weighment slip"],
  ["quality_inspected", "Material quality inspected by QA/QC"],
  ["docs_complete", "Documentation complete (invoice, DC, test certificates)"],
  ["bin_assigned", "Bin location assigned and labelled"],
];

const NewGRNDialog = ({ open, onOpenChange, onCreated }) => {
  const [f, setF] = useState({});
  const [gates, setGates] = useState([]);
  useEffect(() => {
    if (!open) return;
    setF({});
    api.get("/m/gate_entries").then((r) => setGates(r.data.filter((g) => g.status === "At Gate" && g.direction === "Inward")));
  }, [open]);
  const save = async () => {
    try {
      const { data } = await api.post("/grns", { ...compact(f), items: [] });
      invalidateRef("gate_entries");
      toast.success(`${data.grn_no} created`);
      onCreated(data);
      onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const fromGate = !!f.gate_entry_id;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="dialog-new-grn" className={FORM_DIALOG}>
        <DialogHeader><DialogTitle className="font-heading">{fromGate ? "New GRN from Gate Entry" : "New GRN"}</DialogTitle></DialogHeader>
        <div className="space-y-1.5">
          <Label>Vehicle at gate</Label>
          <Select value={f.gate_entry_id || "manual"} onValueChange={(v) => setF(v === "manual" ? {} : { gate_entry_id: v })}>
            <SelectTrigger data-testid="select-gate-entry"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="manual">Manual entry</SelectItem>
              {gates.map((g) => <SelectItem key={g.id} value={g.id}>{g.code} · {g.vehicle_no} · {g.po_ref || "no PO"}</SelectItem>)}
            </SelectContent>
          </Select>
          <p className="text-[11px] text-slate-500">{fromGate ? "Vehicle, time, PO and vendor are taken from the gate entry; PO material is pre-filled as a line item." : "Pick a Purchase Order to auto-fill vendor and line items."}</p>
        </div>
        <FormFields fields={fromGate ? GRN_EXTRA : [...GRN_MANUAL, ...GRN_EXTRA]} form={f} setForm={setF} />
        <DialogFooter><Button onClick={save} data-testid="btn-create-grn">Create GRN</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const HeaderCard = ({ g }) => (
  <div className="grid grid-cols-2 gap-px overflow-hidden rounded-md border border-slate-200 bg-slate-200 sm:grid-cols-4" data-testid="grn-header">
    {[["Vehicle No.", g.vehicle_no], ["Gate Entry Time", fmtDateTime(g.gate_entry_time)], ["PO Reference", g.po_ref], ["Vendor", g.vendor]].map(([l, v]) => (
      <div key={l} className="bg-white p-3">
        <p className="text-[11px] font-medium uppercase tracking-wider text-slate-500">{l}</p>
        <p className="mt-1 font-mono text-sm font-medium text-slate-900">{v || "-"}</p>
      </div>
    ))}
    <div className="col-span-2 bg-white p-3 sm:col-span-4">
      <DetailGrid testId="grn-details" items={[["Site", g.site], ["Challan No.", g.challan_no], ["Invoice No.", g.invoice_no], ["Transporter", g.transporter], ["Received by", g.received_by], ["Gate entry", g.gate_entry_code], ["Remarks", g.remarks]]} />
    </div>
  </div>
);

const ItemsTable = ({ items, editable, onChange, materials }) => {
  const upd = (i, k, v) => onChange(items.map((it, j) => (j === i ? { ...it, [k]: v } : it)));
  const add = () => onChange([...items, { _k: newKey(), item: "", ordered: 0, received: 0, unit: "", bin: "", qa_status: "Pending", rejection_reason: "" }]);
  return (
    <Panel title={`Material line items · ${items.length}`} right={editable && <Button size="sm" variant="outline" onClick={add} data-testid="btn-add-line-item"><Plus className="mr-1 h-3.5 w-3.5" />Add item</Button>}>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            {["Item", "Ordered", "Received", "Unit", "Bin", "QA/QC Status", ""].map((h) => <TableHead key={h} className="text-xs uppercase tracking-wider">{h}</TableHead>)}
          </TableRow></TableHeader>
          <TableBody>
            {items.map((it, i) => (
              <TableRow key={it._k} data-testid={`row-grn-item-${i}`}>
                <TableCell className="min-w-[160px]">
                  {editable ? (
                    <Select value={it.item || undefined} onValueChange={(v) => { const m = materials.find((x) => x.name === v); onChange(items.map((x, j) => (j === i ? { ...x, item: v, unit: x.unit || m?.uom || "" } : x))); }}>
                      <SelectTrigger className="h-8" data-testid={`input-item-name-${i}`}><SelectValue placeholder="Material" /></SelectTrigger>
                      <SelectContent>{[...new Set([it.item, ...materials.map((m) => m.name)].filter(Boolean))].map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : <span className="text-sm font-medium">{it.item}</span>}
                </TableCell>
                {["ordered", "received", "unit", "bin"].map((k) => (
                  <TableCell key={k} className="min-w-[90px]">
                    {editable ? <Input className="h-8 font-mono text-xs" type={["ordered", "received"].includes(k) ? "number" : "text"} value={it[k]} onChange={(e) => upd(i, k, ["ordered", "received"].includes(k) ? Number(e.target.value) : e.target.value)} data-testid={`input-item-${k}-${i}`} />
                      : <span className={`font-mono text-xs ${k === "received" && it.received < it.ordered ? "text-amber-700" : ""}`}>{it[k]}</span>}
                  </TableCell>
                ))}
                <TableCell className="min-w-[130px]">
                  {editable ? (
                    <Select value={it.qa_status} onValueChange={(v) => upd(i, "qa_status", v)}>
                      <SelectTrigger className="h-8" data-testid={`select-qa-status-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{["Pending", "Passed", "Rejected"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : <StatusBadge value={it.qa_status} />}
                </TableCell>
                <TableCell>{editable && <Button size="icon" variant="ghost" onClick={() => onChange(items.filter((_, j) => j !== i))} data-testid={`btn-remove-item-${i}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Panel>
  );
};

const slaText = (submitted, breached) => {
  if (submitted) return "GRN posted";
  return breached ? "SLA breached" : "On track";
};

const SlaCard = ({ day, submitted }) => {
  const breached = !submitted && day > 3;
  return (
    <Panel title="GRN SLA" testId="grn-sla">
      <div className="p-4">
        <p className={`font-heading text-2xl font-bold ${breached ? "text-rose-600" : "text-slate-900"}`}>{submitted ? "Completed" : `Day ${day} of 3`}</p>
        <div className="mt-2 h-1.5 rounded bg-slate-100"><div className={`h-1.5 rounded ${breached ? "bg-rose-500" : "bg-emerald-500"}`} style={{ width: `${Math.min(100, (day / 3) * 100)}%` }} /></div>
        <p className={`mt-1 text-xs ${breached ? "text-rose-600" : "text-emerald-700"}`}>{slaText(submitted, breached)}</p>
      </div>
    </Panel>
  );
};

export default function GRNInspection() {
  const { user } = useAuth();
  const materials = useRefOptions("materials");
  const [grns, setGrns] = useState([]);
  const [sel, setSel] = useState(null);
  const [draft, setDraft] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/grns").then((r) => { setGrns(r.data); setSel((s) => s || r.data[0]?.id); }), []);
  useEffect(() => { load(); }, [load]);
  const g = grns.find((x) => x.id === sel);
  useEffect(() => { if (g) setDraft({ items: g.items.map((it) => ({ ...it, _k: newKey() })), checklist: g.checklist }); }, [g]);

  if (!g || !draft) {
    return (
      <div>
        <PageHeader dept="Store & Site Operations" title="GRN & QA/QC Inspection">{canWrite(user, "stores") && <Button onClick={() => setNewOpen(true)} data-testid="btn-new-grn"><Plus className="mr-1.5 h-4 w-4" />New GRN</Button>}</PageHeader>
        <NewGRNDialog open={newOpen} onOpenChange={setNewOpen} onCreated={(x) => { setGrns((a) => [x, ...a]); setSel(x.id); }} />
      </div>
    );
  }
  const editable = g.status === "draft" && canWrite(user, "stores");
  const replace = (x) => setGrns((a) => a.map((y) => (y.id === x.id ? x : y)));
  const save = async () => {
    const { data } = await api.put(`/grns/${g.id}`, draft);
    replace(data);
    return data;
  };
  const run = async (submit) => {
    setBusy(true);
    try {
      await save();
      if (submit) { replace((await api.post(`/grns/${g.id}/submit`)).data); invalidateRef("purchase_orders"); toast.success(`${g.grn_no} submitted · PO receipt updated`); }
      else toast.success("Draft saved");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const rejected = draft.items.map((it, i) => [it, i]).filter(([it]) => it.qa_status === "Rejected");

  return (
    <div>
      <PageHeader dept="Store & Site Operations" title="GRN & QA/QC Inspection" subtitle="Gate entry → material inspection → GRN posting">
        <Select value={sel} onValueChange={setSel}>
          <SelectTrigger className="w-56" data-testid="select-grn"><SelectValue /></SelectTrigger>
          <SelectContent>{grns.map((x) => <SelectItem key={x.id} value={x.id}>{x.grn_no} · {x.status}</SelectItem>)}</SelectContent>
        </Select>
        {canWrite(user, "stores") && <Button onClick={() => setNewOpen(true)} data-testid="btn-new-grn"><Plus className="mr-1.5 h-4 w-4" />New GRN</Button>}
      </PageHeader>
      <div className="mb-4 flex items-center gap-2"><span className="font-heading text-xl font-bold" data-testid="grn-title">{g.grn_no}</span><StatusBadge value={g.status} testId="grn-status" /><div className="ml-auto"><PrintButton path={`/print/grn/${g.id}`} label="Print GRN" testId="btn-print-grn" /></div></div>
      <div className="grid gap-6 lg:grid-cols-12">
        <div className="min-w-0 space-y-6 lg:col-span-8">
          <HeaderCard g={g} />
          <ItemsTable items={draft.items} editable={editable} materials={materials} onChange={(items) => setDraft((d) => ({ ...d, items }))} />
          {editable && (
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => run(false)} disabled={busy} data-testid="btn-save-grn"><Save className="mr-1.5 h-4 w-4" />Save draft</Button>
              <Button onClick={() => run(true)} disabled={busy} data-testid="btn-submit-grn"><Send className="mr-1.5 h-4 w-4" />Submit GRN</Button>
            </div>
          )}
        </div>
        <div className="min-w-0 space-y-6 lg:col-span-4">
          <Panel title="Inspection checklist · before GRN posting" testId="grn-checklist">
            <div className="space-y-3 p-4">
              {CHECKS.map(([k, l]) => (
                <label key={k} className="flex items-start gap-2 text-sm">
                  <Checkbox checked={!!draft.checklist[k]} disabled={!editable} onCheckedChange={(v) => setDraft((d) => ({ ...d, checklist: { ...d.checklist, [k]: !!v } }))} data-testid={`check-${k}`} className="mt-0.5" />
                  {l}
                </label>
              ))}
            </div>
          </Panel>
          {rejected.map(([it, i]) => (
            <Panel key={i} title={`Rejection reason · ${it.item || "item"}`}>
              <div className="p-4">
                <Textarea rows={2} disabled={!editable} value={it.rejection_reason} placeholder="Reason for rejection"
                  onChange={(e) => setDraft((d) => ({ ...d, items: d.items.map((x, j) => (j === i ? { ...x, rejection_reason: e.target.value } : x)) }))} data-testid={`input-rejection-reason-${i}`} />
              </div>
            </Panel>
          ))}
          <SlaCard day={g.sla_day} submitted={g.status === "submitted"} />
          <Panel title="Attachments · test certificates, invoice, DC" testId="grn-attachments">
            <Attachments entityType="grn" entityId={g.id} writable={editable} canDelete={editable} />
          </Panel>
        </div>
      </div>
      <NewGRNDialog open={newOpen} onOpenChange={setNewOpen} onCreated={(x) => { setGrns((a) => [x, ...a]); setSel(x.id); }} />
    </div>
  );
}
